import { isMembershipCurrentlyActive, parseActiveContext, type SBotActiveContext, type SBotUser } from "@/models/user";

// Per-window workspace pin: when a window is "untethered", its selected
// workspace lives in sessionStorage instead of the shared Firestore
// users/{uid}.activeContext field, so other windows/devices don't follow.

export const UNTETHERED_WORKSPACE_STORAGE_PREFIX = "sbot.untetheredWorkspace.";

function storageKey(userID: string): string {
  return UNTETHERED_WORKSPACE_STORAGE_PREFIX + userID;
}

export function readUntetheredWorkspaceContext(
  userID: string,
  storage: Storage = window.sessionStorage,
): SBotActiveContext | null {
  try {
    const raw = storage.getItem(storageKey(userID));
    if (!raw) return null;
    return parseActiveContext(JSON.parse(raw)) ?? null;
  } catch {
    return null;
  }
}

export function writeUntetheredWorkspaceContext(
  userID: string,
  context: SBotActiveContext | null,
  storage: Storage = window.sessionStorage,
): void {
  try {
    if (context) {
      storage.setItem(storageKey(userID), JSON.stringify(context));
    } else {
      storage.removeItem(storageKey(userID));
    }
  } catch {
    // Storage may be unavailable (private mode, quota); untethering just won't persist.
  }
}

/** Whether the pinned workspace still corresponds to a currently active membership. */
export function pinnedMembershipIsActive(profile: SBotUser, pin: SBotActiveContext): boolean {
  return profile.memberships.some(
    (m) =>
      isMembershipCurrentlyActive(m) &&
      m.targetType === pin.targetType &&
      m.targetID === pin.targetID &&
      m.role === pin.role,
  );
}
